import React from "react";

// ─── Types ──────────────────────────────────────────────────────────────────

export type ApplicationStatus = "OPERATIONAL" | "UNDER_DEVELOPMENT" | "DISPOSITION" | "OTHER";
export type ApplicationCriticality = "CAT-1" | "CAT-2" | "CAT-3" | "CAT-4";

export type AssetRole = "PRIMARY" | "BACKUP" | "STANDBY" | "READ_REPLICA" | "DR" | "ACTIVE" | "PASSIVE";
export type AssetStatus = "RUNNING" | "STOPPED" | "STARTING" | "DEGRADED" | "MAINTENANCE" | "UNKNOWN";
export type AssetProtocol = "TCP" | "UDP" | "HTTP" | "HTTPS" | "GRPC";

// ─── Constants ───────────────────────────────────────────────────────────────

export const STATUS_COLORS: Record<string, string> = {
  // Application Status
  OPERATIONAL: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  UNDER_DEVELOPMENT: "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-400",
  DISPOSITION: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",

  // Asset Status
  RUNNING: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  STOPPED: "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400",
  STARTING: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
  DEGRADED: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
  MAINTENANCE: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
  UNKNOWN: "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400",

  // Generic
  ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  INACTIVE: "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400",
  OTHER: "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400",
};

export const CRITICALITY_COLORS: Record<ApplicationCriticality, string> = {
  "CAT-1": "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
  "CAT-2": "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400",
  "CAT-3": "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400",
  "CAT-4": "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
};

export const ROLE_COLORS: Record<string, string> = {
  PRIMARY: "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400",
  BACKUP: "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400",
  STANDBY: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",
  READ_REPLICA: "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-400",
  DR: "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400",
  ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  PASSIVE: "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400",
};

export const DEVICE_TYPE_COLORS: Record<string, string> = {
  SERVER: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
  VM: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",
  FIREWALL: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
  SWITCH: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
  ROUTER: "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400",
  LB: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  CONTAINER: "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-400",
  STORAGE: "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400",
};

// Shared across CMDB Devices and Assets pages - reuses the same provider
// values as Cloud Accounts (frameworksApi.ts CloudProviderResponse.provider)
export const PLATFORM_OPTIONS: { value: string; label: string }[] = [
  { value: "AWS", label: "AWS" },
  { value: "AZURE", label: "Azure" },
  { value: "GCP", label: "GCP" },
  { value: "OCI", label: "OCI" },
  { value: "ON_PREMISES", label: "On-Premises" },
];

// Shared across CMDB Devices and Assets pages - reuses the same device types
// as the Devices page (frameworksApi.ts DeviceType)
export const ASSET_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "SERVER", label: "Server" },
  { value: "VM", label: "Virtual Machine" },
  { value: "FIREWALL", label: "Firewall" },
  { value: "SWITCH", label: "Switch" },
  { value: "ROUTER", label: "Router" },
  { value: "LB", label: "Load Balancer" },
  { value: "CONTAINER", label: "Container" },
  { value: "STORAGE", label: "Storage" },
];

// Shared across CMDB Devices and Assets pages - reuses the same role values
// as Cloud Accounts (frameworksApi.ts CloudProviderResponse.role)
export const ENVIRONMENT_OPTIONS: { value: string; label: string }[] = [
  { value: "PRODUCTION", label: "Production" },
  { value: "DEVELOPMENT", label: "Development" },
  { value: "STAGING", label: "Staging" },
  { value: "DR", label: "Disaster Recovery" },
  { value: "UAT", label: "UAT" },
  { value: "SANDBOX", label: "Sandbox" },
];

export function optionLabel(
  opts: { value: string; label: string }[],
  v?: string | null,
): string {
  return opts.find((o) => o.value === v)?.label || v || "-";
}

// ─── Components ──────────────────────────────────────────────────────────────

interface StatusBadgeProps {
  status: string | null | undefined;
  label?: string;
  type?: "status" | "criticality" | "role";
  className?: string;
  pill?: boolean;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  label: customLabel,
  type = "status",
  className = "",
  pill = false,
}) => {
  if (!status) return null;
  const s = status.toUpperCase();

  let colorClass = STATUS_COLORS[s] || "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400";

  if (type === "criticality") {
    colorClass = CRITICALITY_COLORS[s as ApplicationCriticality] || colorClass;
  } else if (type === "role") {
    colorClass = ROLE_COLORS[s] || colorClass;
  }

  // Criticality is shown exactly as the API returns it (e.g. "CAT-1"); other
  // types get UNDER_DEVELOPMENT -> Under Development style formatting.
  const label =
    customLabel ??
    (type === "criticality"
      ? status
      : status
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" "));

  const shape = pill
    ? "inline-flex min-w-[110px] items-center justify-center px-3 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap"
    : "px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap";

  return (
    <span className={`${shape} ${colorClass} ${className}`}>
      {label}
    </span>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function autoFillPort(cloudIdentifier: string, currentPort: string): string {
  if (currentPort) return currentPort;
  const id = cloudIdentifier.toLowerCase();
  if (id === "rds" || id === "aurora") return "3306";
  if (id === "elasticache") return "6379";
  if (id === "redshift") return "5439";
  if (id === "dynamodb") return "443";
  return "";
}

export function parseOptionalPort(value: string | number | null | undefined): number | null | undefined {
  if (value === null || value === undefined || value === "") return null;
  const port = Number(value);
  if (!Number.isInteger(port) || port < 1 || port > 65535) {
    return undefined; // Indicates error
  }
  return port;
}

export interface OrgHierarchyNode {
  id: string;
  name?: string;
  orgType?: string;
  isActive?: boolean;
  parentId?: string | null;
  children?: OrgHierarchyNode[];
  [key: string]: unknown;
}

export function flattenOrgHierarchy(nodes: OrgHierarchyNode[], allowedTypes?: string[]): OrgHierarchyNode[] {
  const out: OrgHierarchyNode[] = [];
  for (const n of nodes) {
    if (n.isActive && (!allowedTypes || (n.orgType && allowedTypes.includes(n.orgType)))) {
      out.push(n);
    }
    if (n.children?.length) {
      out.push(...flattenOrgHierarchy(n.children, allowedTypes));
    }
  }
  return out;
}

export const ORG_PICKER_TYPES = ["GROUP", "ORGANIZATION"];

const ORG_TYPE_BADGE: Record<string, { label: string; className: string }> = {
  GROUP: {
    label: "Group",
    className:
      "bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300 ring-violet-200 dark:ring-violet-500/20",
  },
  ORGANIZATION: {
    label: "Org",
    className:
      "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 ring-sky-200 dark:ring-sky-500/20",
  },
};

export function OrgTypeBadge({ orgType }: { orgType: string }) {
  const meta = ORG_TYPE_BADGE[orgType];
  if (!meta) return null;
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-md px-1.5 py-[2px] text-[10px] font-bold leading-none ring-1 ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

/** Org units as dropdown options: the name plus its Group / Org badge. */
export function orgUnitOptions(
  units: { id: string; name: string; orgType: string }[],
) {
  return units.map((o) => ({
    value: o.id,
    label: o.name,
    icon: <OrgTypeBadge orgType={o.orgType} />,
  }));
}

export function ensureOrgTree(nodes: OrgHierarchyNode[]): OrgHierarchyNode[] {
  if (!nodes || nodes.length === 0) return [];
  const hasTreeChildren = nodes.some((n) => n.children && n.children.length > 0);
  if (hasTreeChildren) {
    const allIds = new Set(nodes.map((n) => n.id));
    const isChildOfAny = new Set<string>();
    nodes.forEach((n) => {
      n.children?.forEach((c) => isChildOfAny.add(c.id));
    });
    return nodes.filter(
      (n) => !isChildOfAny.has(n.id) && (!n.parentId || !allIds.has(n.parentId)),
    );
  }

  const map = new Map<string, OrgHierarchyNode>();
  nodes.forEach((n) => map.set(n.id, { ...n, children: [] }));
  const roots: OrgHierarchyNode[] = [];
  nodes.forEach((n) => {
    const current = map.get(n.id)!;
    if (n.parentId && map.has(n.parentId)) {
      map.get(n.parentId)!.children!.push(current);
    } else {
      roots.push(current);
    }
  });
  return roots;
}

export function buildOrgHierarchyOptions(
  rawNodes: OrgHierarchyNode[],
  allowedTypes: string[] = ORG_PICKER_TYPES,
) {
  const tree = ensureOrgTree(rawNodes);

  const collectDescendants = (node: OrgHierarchyNode): string[] => {
    const ids: string[] = [];
    if (node.children?.length) {
      for (const child of node.children) {
        if (child.isActive && (!allowedTypes || (child.orgType && allowedTypes.includes(child.orgType)))) {
          ids.push(child.id);
        }
        ids.push(...collectDescendants(child));
      }
    }
    return ids;
  };

  const options: Array<{
    value: string;
    label: string;
    icon: React.ReactNode;
    depth: number;
    childValues?: string[];
    parentId?: string;
  }> = [];

  const traverse = (node: OrgHierarchyNode, depth: number, parentId?: string) => {
    const isAllowed = node.isActive && (!allowedTypes || (node.orgType && allowedTypes.includes(node.orgType)));
    const childIds = collectDescendants(node);

    if (isAllowed) {
      options.push({
        value: node.id,
        label: node.name || node.id,
        icon: <OrgTypeBadge orgType={node.orgType || ""} />,
        depth,
        childValues: childIds.length > 0 ? childIds : undefined,
        parentId,
      });
    }

    if (node.children?.length) {
      const nextDepth = isAllowed ? depth + 1 : depth;
      for (const child of node.children) {
        traverse(child, nextDepth, isAllowed ? node.id : parentId);
      }
    }
  };

  for (const root of tree) {
    traverse(root, 0);
  }

  return options;
}

export function topologyInitialZoom(
  nodeCount: number,
  opts?: { minZoom?: number; maxZoom?: number },
): number {
  const minZoom = opts?.minZoom ?? 0.05;
  const maxZoom = opts?.maxZoom ?? 2.5;

  const target =
    nodeCount <= 3
      ? 1.5
      : nodeCount <= 8
        ? 1.25
        : nodeCount <= 20
          ? 1.05
          : nodeCount <= 40
            ? 0.85
            : nodeCount <= 80
              ? 0.7
              : 0.55;

  return Math.min(maxZoom, Math.max(minZoom, target));
}

export const PIN_HEAD = "sticky top-0 z-30 bg-[#eef2f7] dark:bg-[#262738]";
export const PIN_CELL =
  "sticky z-10 bg-white dark:bg-[#131320] group-hover:bg-[#f1f5fe] dark:group-hover:bg-[#23232f]";
export const PIN_EDGE =
  "after:pointer-events-none after:absolute after:inset-y-0 after:-right-px after:w-px " +
  "after:bg-[#e2e8f0] dark:after:bg-white/[0.06] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)] dark:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.3)]";
